import { translate } from '@jsverse/transloco';
import { OrderListItem } from '../interfaces/order-list.model';
import { OrderProposal, OrderSection } from '../interfaces/order-proposal.model';

// Più codici cliente e nessuno scelto: si chiede quale usare
export function sectionNeedsCodeChoice(section: OrderSection): boolean {
  return section.codiciCliente.length > 1 && !section.idCliente;
}

export function proposalNeedsCodeChoice(proposal: OrderProposal): boolean {
  return !!proposal.idBusinessPartner && (sectionNeedsCodeChoice(proposal.oil) || sectionNeedsCodeChoice(proposal.nonOil));
}

// Codice da proporre nella scelta: quello scelto, altrimenti l'ultimo utilizzato
export function sectionProposedCode(section: OrderSection): string | null {
  const code = section.codiceCliente ?? section.codiciCliente.find(c => c.ultimoUtilizzato)?.codiceCliente;
  return code != null ? String(code) : null;
}

// Proposta ancora modificabile: le altre si aprono nel dettaglio ordine
export function isDraftProposal(proposal: OrderProposal): boolean {
  return proposal.stato === 'Nuovo' || proposal.stato === 'Bozza';
}

export type OrderTagSource = Pick<OrderListItem, 'statoApp' | 'statoErp' | 'inAutorizzazione' | 'bloccoAmministrativo' | 'bloccoStock'>;

// La chiave UI resta stabile al cambio lingua: il badge la traduce e il colore usa la stessa chiave.
// Tag di riga: annullata o respinta, poi l'autorizzazione, poi il blocco, poi lo stato ERP o quello in App
export function orderTagLabel(item: OrderTagSource): string {
  if (item.statoApp === 'Respinta' || item.statoApp === 'Annullata' || item.statoErp === 'Cancelled') {
    return 'orders.ui.cancellato';
  }
  if (item.inAutorizzazione) {
    return 'orders.ui.inApprovazione';
  }
  if (item.bloccoAmministrativo) {
    return 'orders.ui.bloccoAmministrativo';
  }
  if (item.bloccoStock) {
    return 'orders.ui.bloccoStock';
  }

  switch (item.statoErp) {
    case 'Confirmed':
      return 'orders.ui.confermato';
    case 'InPreparation':
      return 'orders.ui.inPreparazione';
    case 'Shipped':
      return 'orders.ui.spedito';
    case 'Invoiced':
      return 'orders.ui.fatturata';
    case 'Closed':
      return 'orders.ui.chiuso';
    default:
      return 'orders.ui.proposta';
  }
}

// Colore del tag come nel Flutter: proposta e autorizzazione in attesa, confermati e in preparazione informativi,
// spediti e oltre conclusi, annullati e blocchi in errore
const ORDER_TAG_TONES: Record<string, string> = {
  'orders.ui.proposta': 'warning',
  'orders.ui.inApprovazione': 'warning',
  'orders.ui.confermato': 'info',
  'orders.ui.inPreparazione': 'info',
  'orders.ui.spedito': 'success',
  'orders.ui.fatturata': 'success',
  'orders.ui.chiuso': 'success',
  'orders.ui.cancellato': 'error',
  'orders.ui.bloccoAmministrativo': 'error',
  'orders.ui.bloccoStock': 'error',
};

export function orderTagTone(tag: string): string {
  return ORDER_TAG_TONES[tag] ?? 'info';
}

// Con più sotto-ordini il tag della riga è il peggiore e il tooltip elenca quello di ognuno, come nel Flutter
export function subOrdersTooltip(item: OrderListItem): string {
  if (item.commesse.length < 2) {
    return '';
  }

  return item.commesse
    .map(c => translate<string>('orders.ui.sottoOrdineStato', { number: c.numeroCommessa ?? '', status: translate(orderTagLabel({ ...c, statoApp: null, statoErp: c.stato })).toLowerCase() }))
    .join('\n');
}

// Le commesse nate in ERP non hanno il numero dell'ordine in App: si mostrano i numeri di commessa
export function orderNumberLabel(item: OrderListItem): string {
  return item.numero != null ? String(item.numero) : item.numeriCommessa.join(', ');
}
